import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Flex,
  Form,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import type { TableTransferDataType } from '../TableTransfer';
import TableTransfer from '../TableTransfer';
import type { User } from '../../Types/interfaces';
import {
  AssignStudentsToSupervisor,
  GetStudyLinesOfSupervisor,
  GetStudyLinesOfUniversityCoordinator,
  GetSupervisorsOfStudyLine,
  GetUserById,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useInternshipProcessesOfStudyLine } from '../../hooks/useInternshipProcesses';
import { isUserDataFilled } from '../../helper/userHelper';
import { useCurrentStudyYear } from '../../hooks/useStudyYear';
import { StudyYearDropdown } from '../StudyYearDropdown';

interface SupervisorAssignmentComponentProps {
  isCoordinatorMenu: boolean;
}

const SupervisorAssignmentComponent = ({
  isCoordinatorMenu: isCoordinatorMenu,
}: SupervisorAssignmentComponentProps): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [targets, setTargets] = useState<TableTransferDataType[]>([]);
  const { t } = useTranslation();
  const selectedSupervisorId = Form.useWatch('supervisor', form);
  const selectedStudyLineId = Form.useWatch('studyLine', form);
  const queryClient = useQueryClient();
  // in the supervisor menu the superviser is the logged user and assign yourself to the student
  const supervisorId = isCoordinatorMenu
    ? selectedSupervisorId
    : loggedInUser.id;

  // undefined until the user selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  // only the study lines of the shown study year can be chosen
  const { data: studyLines = EMPTY_ARRAY } = useQuery({
    queryKey: [
      ...QUERY_KEYS.STUDY_LINES_OF_UNIVERSITY_COORDINATOR,
      loggedInUser.id,
      isCoordinatorMenu,
      shownStudyYearId,
    ],
    queryFn: async () => {
      if (isCoordinatorMenu) {
        return await GetStudyLinesOfUniversityCoordinator(
          loggedInUser.id.toString(),
          shownStudyYearId
        );
      }
      return await GetStudyLinesOfSupervisor(
        loggedInUser.id.toString(),
        shownStudyYearId
      );
    },
    enabled: !!loggedInUser.id && shownStudyYearId !== undefined,
  });

  // another study year has other study lines, so the chosen ones are cleared
  const changeStudyYear = (studyYearId: number): void => {
    setSelectedStudyYearId(studyYearId);
    form.setFieldsValue({ studyLine: undefined, supervisor: undefined });
    setTargets([]);
  };

  const selectedStudyLine = studyLines.find(
    (studyLine) => studyLine.id === selectedStudyLineId
  );

  useEffect(() => {
    if (studyLines.length > 0 && !selectedStudyLineId) {
      form.setFieldValue('studyLine', studyLines[0].id);
    }
  }, [studyLines, selectedStudyLineId, form]);

  const {
    data: processesOfStudyLine = EMPTY_ARRAY,
    isError: processesOfStudyLineError,
  } = useInternshipProcessesOfStudyLine(selectedStudyLineId);

  useEffect(() => {
    if (processesOfStudyLineError) {
      messageApi.error(t('supvisAssignComp.fetchError'));
    }
  }, [messageApi, processesOfStudyLineError, t]);

  const { data: supervisorsOfStudyLine = EMPTY_ARRAY } = useQuery({
    queryKey: [...QUERY_KEYS.SUPERVISORS_OF_STUDY_LINE, selectedStudyLineId],
    queryFn: async () => {
      return await GetSupervisorsOfStudyLine(String(selectedStudyLineId));
    },
    enabled: !!selectedStudyLineId,
    meta: {
      onError: () => {
        messageApi.error(t('supvisAssignComp.fetchError'));
      },
    },
  });

  useEffect(() => {
    if (supervisorsOfStudyLine.length == 1 && selectedStudyLineId) {
      form.setFieldValue('supervisor', supervisorsOfStudyLine[0].id);
    }
    if (supervisorsOfStudyLine.length == 0) {
      form.setFieldValue('supervisor', undefined);
    }
  }, [form, selectedStudyLineId, supervisorsOfStudyLine]);

  const assignSupervisorMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const internshipProcessIds = targets.map((t) => t.process.id);

      if (targets.map((t) => t.tutor.id).includes(selectedSupervisorId)) {
        messageApi.open({
          type: 'error',
          content: t('supvisAssignComp.tutorAndSupvisCantBeSame'),
        });
        return;
      }

      const user = await GetUserById(supervisorId);
      if (!isUserDataFilled(user)) {
        messageApi.warning(t('tutorAssignmentComp.userDataMissingWarning'), 7);
        return;
      }

      await AssignStudentsToSupervisor(
        selectedStudyLineId.toString(),
        supervisorId,
        internshipProcessIds
      );

      setTargets([]);

      messageApi.open({
        type: 'success',
        content: t('supvisAssignComp.assignSuccess'),
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROCESSES_BY_UNIVERSITY_DEPARTMENT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROCESSES_BY_STUDY_LINE,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PENDING_CONVENTIONS,
      });
    },
    onError: (error: Error) => {
      console.error(error);
      messageApi.open({
        type: 'error',
        content: t('supvisAssignComp.assignError'),
      });
    },
  });

  const getUsername = (user: User): string => {
    if (user.firstName === null) {
      if (user.lastName === null) {
        return user.email;
      }

      return user.lastName.trim() || user.email;
    }

    if (user.lastName === null) {
      return user.firstName.trim() || user.email;
    }

    return `${user.lastName} ${user.firstName}`.trim();
  };

  return (
    <>
      <br />
      {contextHolder}
      <Flex
        align="center"
        justify="start"
        gap={8}
        wrap
        style={{ marginBottom: 16 }}
      >
        <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
        <StudyYearDropdown
          value={selectedStudyYearId}
          onChange={changeStudyYear}
        />
      </Flex>
      {isCoordinatorMenu && selectedStudyLine && (
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 20 }}>
          {t('supvisAssignComp.yourDep')}: {selectedStudyLine.departmentName}
        </Typography.Title>
      )}
      <Form form={form} size="large">
        <Row gutter={32}>
          <Col xs={24} lg={isCoordinatorMenu ? 12 : 24}>
            <Form.Item name="studyLine">
              <Select
                size="large"
                placeholder={t('supvisAssignComp.selStudyLine')}
                options={studyLines.map((studyLine) => ({
                  label: studyLine.name,
                  value: studyLine.id,
                }))}
              />
            </Form.Item>
          </Col>

          {isCoordinatorMenu && (
            <Col xs={24} lg={12}>
              <Form.Item name="supervisor">
                <Select
                  size="large"
                  placeholder={t('supvisAssignComp.selSupvis')}
                  options={supervisorsOfStudyLine.map((u) => ({
                    label: getUsername(u),
                    value: u.id,
                  }))}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Form.Item>
          <TableTransfer
            processes={processesOfStudyLine}
            setTargets={setTargets}
            selectedSupervisorId={supervisorId}
            studyLineId={selectedStudyLineId}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            disabled={
              targets.length === 0 || assignSupervisorMutation.isPending
            }
            loading={assignSupervisorMutation.isPending}
            onClick={() => assignSupervisorMutation.mutate()}
            block
          >
            {t('supvisAssignComp.submit')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default SupervisorAssignmentComponent;
